/**
 * The city picker's events, built in one place because the header and the
 * mobile menu each render their own picker and were reporting the change
 * through a method of their own.
 *
 * The two report it under different categories, which is the only thing that
 * tells a header pick from a drawer pick once the events are in GA4, so the
 * category stays a parameter rather than becoming a shared constant.
 */

interface CityTrackingLocation {
  /** Reads in a report as it always has; already mapped in GTM. */
  changedCategory: string;
  /** Which of the two pickers this is, for the open event's label. */
  label: string;
}

export const CITY_SELECT = {
  header: {
    changedCategory: "Header City Changed",
    label: "Header",
  },
  mobileMenu: {
    changedCategory: "Mobile Menu City Changed",
    label: "Mobile menu",
  },
} satisfies Record<string, CityTrackingLocation>;

export type TCitySelectLocation = keyof typeof CITY_SELECT;

/**
 * Where a service was clicked. The drawer's shortcut is kept apart from the
 * panel inside it: it is one tap with the picker never opened, and whether that
 * shortcut earns its place is a question only its own number can answer.
 */
export const CITY_SERVICE_FROM = {
  header: "Header",
  mobileMenu: "Mobile menu",
  mobileMenuShortcut: "Mobile menu shortcut",
} as const;

export type TCityServiceFrom = keyof typeof CITY_SERVICE_FROM;

/**
 * The names phone number clicks already report verticals under — see
 * PHONE_NUMBER_TYPES in phoneNumberTracking — so a report can hold a service
 * click and a call to the same vertical side by side. The keys are the city
 * links' own, which call law `lawyer`.
 */
const SERVICE_NAMES: Record<string, string> = {
  funeral: "Funeral",
  lawyer: "Law",
  therapy: "Therapy",
};

function push(payload: Record<string, unknown>): void {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

/**
 * A city was picked. The payload is unchanged from the two methods this
 * replaced — the same event, the same category per picker, the same two keys —
 * so the reports built on it keep reading.
 */
export function pushNewCitySelected(
  location: TCitySelectLocation,
  to: string,
  from: string
): void {
  push({
    changedCityFrom: from,
    changedCityTo: to,
    event: "new_city_selected",
    eventCategory: CITY_SELECT[location].changedCategory,
  });
}

/**
 * The picker was opened. Worth its own event because a change alone cannot tell
 * a picker nobody opens from one that is opened and abandoned, and the two call
 * for opposite fixes.
 */
export function pushCitySelectOpen(location: TCitySelectLocation): void {
  push({
    event: "city_select_open",
    eventCategory: "City select",
    eventLabel: CITY_SELECT[location].label,
    eventPageUrl: window.location.href,
  });
}

/**
 * A service was entered from the city picker.
 *
 * `cityChanged` is what makes the event worth having: it separates the visitor
 * who looked up another city and then picked a service there from the one who
 * only wanted a different service in the city they were already in. Both end on
 * a service page, and they are asking for different things. 0 and 1 rather than
 * a boolean, as phone number clicks report a swapped number.
 */
export function pushCityServiceClick(
  from: TCityServiceFrom,
  service: { city: string; key: string },
  cityChanged: boolean
): void {
  push({
    cityChanged: cityChanged ? 1 : 0,
    event: "city_service_click",
    eventCategory: "City select",
    eventLabel: CITY_SERVICE_FROM[from],
    eventPageUrl: window.location.href,
    serviceCity: service.city,
    serviceKey: SERVICE_NAMES[service.key] ?? service.key,
  });
}
